import React, { useState } from 'react';
import { MapPin, Calendar, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from '../hooks/use-toast';

const BookingForm = ({ isOpen, onClose }) => {
  const [bookingData, setBookingData] = useState({
    serviceType: '',
    pickupLocation: '',
    dropoffLocation: '',
    date: '',
    time: '',
    passengers: '1',
    name: '',
    email: '',
    phone: ''
  });

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!bookingData.serviceType || !bookingData.pickupLocation || !bookingData.date || !bookingData.time || !bookingData.name || !bookingData.email || !bookingData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Mock booking submission
    console.log('Booking submitted:', bookingData);
    toast({
      title: "Booking Request Received!",
      description: "Our team will contact you shortly to confirm your reservation.",
    });
    
    // Reset form
    setBookingData({
      serviceType: '',
      pickupLocation: '',
      dropoffLocation: '',
      date: '',
      time: '',
      passengers: '1',
      name: '',
      email: '',
      phone: ''
    });
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-amber-500">Book Your Ride</DialogTitle>
          <DialogDescription className="text-gray-300">
            Complete the form below and we'll confirm your executive transportation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Service Type */}
          <div className="space-y-2">
            <Label htmlFor="serviceType" className="text-white">Service Type *</Label>
            <Select value={bookingData.serviceType} onValueChange={(value) => handleInputChange('serviceType', value)}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Select service type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 text-white">
                <SelectItem value="airport">Airport Transfer</SelectItem>
                <SelectItem value="hourly">Hourly Service</SelectItem>
                <SelectItem value="city-to-city">City-to-City</SelectItem>
                <SelectItem value="corporate">Corporate Travel</SelectItem>
                <SelectItem value="event">Special Event</SelectItem>
                <SelectItem value="aviation">Private Aviation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pickup Location */}
          <div className="space-y-2">
            <Label htmlFor="pickupLocation" className="text-white">Pickup Location *</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="pickupLocation"
                placeholder="Enter pickup address"
                value={bookingData.pickupLocation}
                onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
                className="pl-10 bg-slate-900 border-slate-600 text-white placeholder:text-gray-400"
              />
            </div>
          </div>

          {/* Dropoff Location */}
          <div className="space-y-2">
            <Label htmlFor="dropoffLocation" className="text-white">Dropoff Location {bookingData.serviceType !== 'hourly' && '*'}</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="dropoffLocation"
                placeholder="Enter destination address"
                value={bookingData.dropoffLocation}
                onChange={(e) => handleInputChange('dropoffLocation', e.target.value)}
                className="pl-10 bg-slate-900 border-slate-600 text-white placeholder:text-gray-400"
                disabled={bookingData.serviceType === 'hourly'}
              />
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-white">Date *</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="date"
                  type="date"
                  value={bookingData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-600 text-white"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="time" className="text-white">Time *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="time"
                  type="time"
                  value={bookingData.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-600 text-white"
                />
              </div>
            </div>
          </div>

          {/* Passengers */}
          <div className="space-y-2">
            <Label htmlFor="passengers" className="text-white">Number of Passengers</Label>
            <Select value={bookingData.passengers} onValueChange={(value) => handleInputChange('passengers', value)}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 text-white">
                <SelectItem value="1">1 Passenger</SelectItem>
                <SelectItem value="2">2 Passengers</SelectItem>
                <SelectItem value="3">3 Passengers</SelectItem>
                <SelectItem value="4">4 Passengers</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Contact Information */}
          <div className="border-t border-slate-600 pt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Contact Information</h3>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Full Name *</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={bookingData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-400"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={bookingData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={bookingData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold text-lg py-6 rounded-lg shadow-lg shadow-amber-500/20 transition-all duration-300"
          >
            Confirm Booking
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingForm;